import { useReducer, useState } from 'react';
import { FormField, TextInput, TextArea, Select } from 'grommet';
import { HelpWith } from '@components/basicComponents';
import { HelpWithType } from '@customTypes/enums';
import type { FormFieldsProps } from '@customTypes/types';
import type { GreenLakeSupportGLCPFormFieldsState } from '@customTypes/stateTypes';
import { helpWithOptions, localGatewayOptions } from './GreenLakeSupportGLCPFormOptions';

export const GreenLakeSupportGLCPFormFields = (props: FormFieldsProps): JSX.Element => {
  const [state, updateState] = useReducer(
    (prev: GreenLakeSupportGLCPFormFieldsState, newState: Partial<GreenLakeSupportGLCPFormFieldsState>) => ({
      ...prev,
      ...newState,
    }),
    {
      helpWith: '',
      showLocalGatewayId: false,
    }
  );

  const helpWithChange = (option: string): void => {
    if (option === state.helpWith) {
      return;
    }

    clearFirstSelectState();
    updateState({ helpWith: option });

    switch (option) {
      case helpWithOptions.localGateway:
      case helpWithOptions.dashboardAndMonitoring:
        props.onDetailsChange({ numFiles: 0 });
        break;
      case helpWithOptions.newSubscriptionOrder:
      case helpWithOptions.other:
        props.onDetailsChange({ showDetails: true, isRequiredDetails: true });
        break;
      default:
        props.onDetailsChange({ showDetails: true });
    }
  };

  const helpWithSecondaryChange = (option: string): void => {
    clearSecondSelectState();

    if (state.helpWith === helpWithOptions.localGateway && option !== 'Other') {
      updateState({ showLocalGatewayId: true });
    }

    props.onDetailsChange({ showDetails: true });
  };

  const clearState = (): void => {
    clearFirstSelectState();
    updateState({ helpWith: '' });
    setSelectedApiRegion('');
    setAccountRelationType('');
  };

  props.onReset && props.onReset(clearState);

  const clearFirstSelectState = (): void => {
    clearSecondSelectState();
    props.onDetailsChange({ showDetails: false, isRequiredDetails: false });
  };

  const clearSecondSelectState = (): void => {
    updateState({ showLocalGatewayId: false });
  };

  const [accountRelationType, setAccountRelationType] = useState('');
  const [selectedApiRegion, setSelectedApiRegion] = useState('');

  return (
    <>
      <HelpWith
        selectType={!props.isConsolidated ? HelpWithType.First : HelpWithType.Second}
        options={Object.values(helpWithOptions)}
        onChange={helpWithChange}
      />

      {state.helpWith === helpWithOptions.userManagement &&
        <FormField name="username" htmlFor="username" label="Username" required>
          <TextInput id="username" name="username" placeholder="Username" />
        </FormField>
      }

      {state.helpWith === helpWithOptions.deviceManagement  &&
        <FormField name="serialNumber" htmlFor="serial-number" label="Serial number" required>
          <TextInput id="serial-number" name="serialNumber" placeholder="Serial number" />
        </FormField>
      }

      {state.helpWith === helpWithOptions.workspaceManagement &&
        <FormField name="workspaceName" htmlFor="workspace-name" label="Workspace name" required>
          <TextInput id="workspace-name" name="workspaceName" placeholder="Workspace name" />
        </FormField>
      }

      {state.helpWith === helpWithOptions.applicationProvisioning &&
        <FormField name="applicationName" htmlFor="application-name" label="Application name" required>
          <TextInput id="application-name" name="applicationName" placeholder="Application name" />
        </FormField>
      }

      {state.helpWith === helpWithOptions.subscriptionManagement &&
        <FormField name="subscriptionId" htmlFor="subscription-id" label="Subscription ID" required>
          <TextInput id="subscription-id" name="subscriptionId" placeholder="Subscription ID" />
        </FormField>
      }

      {state.helpWith === helpWithOptions.api &&
        <FormField name="workspaceId" htmlFor="workspace-id" label="Workspace ID" required>
          <TextInput id="workspace-id" name="workspaceId" placeholder="Workspace ID" />
        </FormField>
      }
      {state.helpWith === helpWithOptions.commerceApi &&
        <FormField name="hpeAccMgrName" htmlFor="hpe-acc-mgr-name" label="HPE Account Manager or HPE Sales Name">
          <TextInput id="hpe-acc-mgr-name" name="hpeAccMgrName" placeholder="Name" />
        </FormField>
      }
      {state.helpWith === helpWithOptions.commerceApi &&
        <FormField name="hpeAccMgrEmail" htmlFor="hpe-acc-mgr-email" label="HPE Account Manager or HPE Sales Email ID">
          <TextInput id="hpe-acc-mgr-email" name="hpeAccMgrEmail" placeholder="Email ID" />
        </FormField>
      }
      {state.helpWith === helpWithOptions.commerceApi &&
        <FormField name="hpePartyId" htmlFor="hpe-party-id" label="HPE Sold To Party ID’s to be used by API">
          <TextArea id="hpe-party-id" name="hpePartyId" placeholder="Party ID" resize="vertical" style={{ minHeight:'58px' }}/>
        </FormField>
      }
      {state.helpWith === helpWithOptions.commerceApi &&
        <FormField name="apiRegion" htmlFor="api-region" label="Region of the API">
          <Select
            id="api-region"
            name="apiRegion"
            placeholder="Select"
            options={["Global", "AMS", "APJ", "EMEA", "LATAM"]}
            value={selectedApiRegion}
            onChange={({ option }) => setSelectedApiRegion(option)}
          />
        </FormField>
      }
      {state.helpWith === helpWithOptions.commerceApi &&
        <FormField htmlFor="acc-rel-type" name="accRelType" label="Are you a Customer, Reseller, Distributor or other?">
          <Select
            id="acc-rel-type"
            name="accRelType"
            placeholder="Select"
            options={["Customer", "Reseller", "Distributor", "Other"]}
            value={accountRelationType}
            onChange={({ option }) => setAccountRelationType(option)}
          />
        </FormField>
      }

      {state.helpWith === helpWithOptions.localGateway &&
        <HelpWith
          selectType={!props.isConsolidated ? HelpWithType.Second : HelpWithType.Third }
          options={localGatewayOptions}
          onChange={helpWithSecondaryChange}
        />
      }

      {state.showLocalGatewayId &&
        <FormField name="localGatewayId" htmlFor="local-gateway-id" label="Local Gateway ID" required>
          <TextInput id="local-gateway-id" name="localGatewayId" placeholder="Local Gateway ID" />
        </FormField>
      }
    </>
  );
};
